(() => {
  'use strict';
  const boot = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    root.classList.add('is-interactive');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 835px)');
    const gallery = root.querySelector('[data-gallery]');
    const galleryImage = root.querySelector('[data-gallery-image]');
    const thumbnails = Array.from(root.querySelectorAll('[data-gallery-thumbnail]'));
    const imageDialog = root.querySelector('[data-image-dialog]');
    let galleryIndex = 0;
    const setGallery = (index) => {
      galleryIndex = (index + thumbnails.length) % thumbnails.length;
      const selected = thumbnails[galleryIndex];
      if (!selected) return;
      galleryImage.src = selected.dataset.imageSrc;
      const dialogImage = imageDialog && imageDialog.querySelector('[data-dialog-image]');
      if (dialogImage) dialogImage.src = selected.dataset.imageSrc;
      thumbnails.forEach((thumbnail, i) => {
        const active = i === galleryIndex;
        thumbnail.classList.toggle('is-selected', active);
        thumbnail.setAttribute('aria-pressed', String(active));
      });
      const album = selected.parentElement;
      const albumBounds = album?.getBoundingClientRect();
      const selectedBounds = selected.getBoundingClientRect();
      if (albumBounds && selectedBounds.left < albumBounds.left) album.scrollLeft -= albumBounds.left - selectedBounds.left;
      else if (albumBounds && selectedBounds.right > albumBounds.right) album.scrollLeft += selectedBounds.right - albumBounds.right;
      const status = root.querySelector('[data-gallery-status]');
      if (status) status.textContent = `${galleryIndex + 1} / ${thumbnails.length}`;
    };
    thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => setGallery(index)));
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => setGallery(galleryIndex - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => setGallery(galleryIndex + 1));
    gallery?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); setGallery(galleryIndex + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); setGallery(galleryIndex - 1); }
    });
    const addSwipe = (element, callback) => {
      let start = null;
      element?.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse') return;
        start = {x: event.clientX, y: event.clientY};
      });
      element?.addEventListener('pointerup', event => {
        if (!start) return;
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;
        start = null;
        if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
          element.dataset.lastSwipeAt = String(Date.now());
          callback(deltaX < 0 ? 1 : -1);
        }
      });
      element?.addEventListener('pointercancel', () => { start = null; });
    };
    addSwipe(gallery, direction => setGallery(galleryIndex + direction));
    let previousPageOverflow = null;
    root.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => {
      if (Date.now() - Number(gallery?.dataset.lastSwipeAt || 0) < 350) return;
      if (typeof imageDialog?.showModal !== 'function' || imageDialog.open) return;
      imageDialog.showModal();
      previousPageOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    });
    root.querySelector('[data-image-dialog-close]')?.addEventListener('click', () => imageDialog.close());
    imageDialog?.addEventListener('close', () => {
      if (previousPageOverflow === null) return;
      document.documentElement.style.overflow = previousPageOverflow;
      previousPageOverflow = null;
    });
    imageDialog?.addEventListener('click', event => {
      if (event.target !== imageDialog) return;
      const box = imageDialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) imageDialog.close();
    });
    const groups = Array.from(root.querySelectorAll('[data-product-group]'));
    const openGroup = (group, open) => {
      group.classList.toggle('is-open', open);
      const toggle = group.querySelector('[data-group-toggle]');
      toggle?.setAttribute('aria-expanded', String(open));
      const icon = toggle?.querySelector('.zo-toggle-icon');
      if (icon) icon.textContent = open ? '−' : '+';
    };
    groups.forEach(group => group.querySelector('[data-group-toggle]')?.addEventListener('click', () => openGroup(group, !group.classList.contains('is-open'))));
    const scrollToTarget = (target) => {
      const group = target.closest('[data-product-group]');
      if (mobile.matches && group) openGroup(group, true);
      requestAnimationFrame(() => target.scrollIntoView({behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start'}));
    };
    root.addEventListener('click', event => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      if (link.hasAttribute('data-zoleo-info')) return;
      const hash = link.getAttribute('href');
      if (hash.length <= 1) return;
      const target = document.getElementById(hash.slice(1));
      if (!target) return;
      event.preventDefault();
      scrollToTarget(target);
      history.replaceState(null, '', hash);
    });
    document.addEventListener('zoleo:open-section', event => {
      const target = document.getElementById(event.detail?.id);
      if (target && root.contains(target)) scrollToTarget(target);
    });
    window.addEventListener('hashchange', () => {
      const target = document.getElementById(location.hash.slice(1));
      if (target && root.contains(target)) scrollToTarget(target);
    });
    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      const group = target?.closest('[data-product-group]');
      if (group) {
        openGroup(group, true);
        requestAnimationFrame(() => target.scrollIntoView({behavior: 'auto', block: 'start'}));
      }
    }
    const anchors = Array.from(root.querySelectorAll('[data-anchor-nav] a'));
    if ('IntersectionObserver' in window) {
      const sections = anchors.map(anchor => document.getElementById(anchor.hash.slice(1))?.closest('[data-product-group]')).filter(Boolean);
      const visibleGroups = new Set();
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) visibleGroups.add(entry.target);
          else visibleGroups.delete(entry.target);
        });
        const visible = Array.from(visibleGroups).sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
        if (!visible) return;
        anchors.forEach(anchor => anchor.classList.toggle('is-active', anchor.hash === '#' + visible.dataset.productGroup));
      }, {rootMargin: '-150px 0px -45% 0px', threshold: 0});
      sections.forEach(section => observer.observe(section));
    }
    let reviewsIndex = 0;
    const reviewTrack = root.querySelector('[data-review-track]');
    const reviewCards = Array.from(root.querySelectorAll('[data-review-card]'));
    const reviewPrev = root.querySelector('[data-review-prev]');
    const reviewNext = root.querySelector('[data-review-next]');
    const reviewDots = Array.from(root.querySelectorAll('[data-review-dot]'));
    const setReview = (index) => {
      reviewsIndex = Math.max(0, Math.min(reviewCards.length - 1, index));
      if (reviewTrack) reviewTrack.style.transform = mobile.matches ? `translateX(-${reviewsIndex * 100}%)` : '';
      if (reviewPrev) reviewPrev.disabled = reviewsIndex === 0;
      if (reviewNext) reviewNext.disabled = reviewsIndex === reviewCards.length - 1;
      reviewDots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === reviewsIndex);
        dot.setAttribute('aria-pressed', String(i === reviewsIndex));
      });
    };
    reviewPrev?.addEventListener('click', () => setReview(reviewsIndex - 1));
    reviewNext?.addEventListener('click', () => setReview(reviewsIndex + 1));
    reviewDots.forEach(dot => dot.addEventListener('click', () => setReview(Number(dot.dataset.reviewDot))));
    const reviewCarousel = root.querySelector('[data-review-carousel]');
    reviewCarousel?.addEventListener('keydown', event => {
      if (!mobile.matches) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); setReview(reviewsIndex + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); setReview(reviewsIndex - 1); }
    });
    addSwipe(reviewCarousel, direction => { if (mobile.matches) setReview(reviewsIndex + direction); });
    mobile.addEventListener('change', () => setReview(reviewsIndex));
    const variantData = root.querySelector('[data-product-variants]');
    let variants = [];
    try { variants = JSON.parse(variantData?.textContent || '[]'); } catch (_) { /* Keep server-rendered variant state. */ }
    const setVariant = id => {
      const variant = variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      const variantInput = root.querySelector('[data-selected-variant]');
      if (variantInput) variantInput.value = variant.id;
      const price = root.querySelector('[data-product-price]');
      if (price) price.textContent = variant.formatted_price;
      const sku = root.querySelector('[data-product-sku]');
      if (sku) sku.textContent = variant.sku || '';
      const buy = root.querySelector('[data-buy-button]');
      if (buy) buy.disabled = !variant.available;
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(null, '', url);
      root.dispatchEvent(new CustomEvent('zoleo:variant-change', {bubbles: true, detail: variant}));
    };
    root.querySelector('[data-variant-select]')?.addEventListener('change', event => setVariant(event.target.value));
    setReview(0);
  };
  const init = () => document.querySelectorAll('[data-zoleo-product]').forEach(boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
  document.addEventListener('shopify:section:load', init);
})();
